// Chamadas do pipeline de licitações (actions `lic_*` em /comercial/licitacoes).
// Uma função por ação, todas devolvendo o corpo já aberto; o erro vem com a
// frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../../config';
import { getToken } from '../../../utils/auth';
import type { LicitacaoPipeline } from './saudeLicitacao';

async function post<T = any>(body: Record<string, any>): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/licitacoes`, body, { headers: { Authorization: token }, timeout: 25000 });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.erro || e?.message || 'erro desconhecido';

export interface Vendedor { nome: string; email: string }

export interface EventoLicitacao {
  id: number;
  tipo: string;
  campo: string | null;
  valor_antigo: string | null;
  valor_novo: string | null;
  resumo: string | null;
  material: boolean;
  origem: string | null;
  criado_por: string | null;
  criado_em: string;
  data_referencia: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
}

export interface Acompanhamento {
  oportunidade_id: number;
  /** Só licitação ABERTA no quadro é acompanhada. */
  acompanhada: boolean;
  referencia: string | null;
  referencia_origem: string | null;
  link_pncp: string | null;
  situacao_fonte: string | null;
  resultado_publicado: boolean;
  verificado_em: string | null;
  ok_em: string | null;
  erro: string | null;
  eventos: EventoLicitacao[];
}

export const licApi = {
  pipeline: () => post<LicitacaoPipeline[]>({ action: 'lic_pipeline' }),
  avaliar: (id: number, responsavel_email?: string) =>
    post<LicitacaoPipeline>({ action: 'lic_avaliar', id, responsavel_email }),
  decidir: (id: number, participar: boolean, motivo?: string, responsavel_email?: string) =>
    post<LicitacaoPipeline>({ action: 'lic_decidir', id, participar, motivo, responsavel_email }),
  mover: (id: number, para: string, extra: Record<string, any> = {}) =>
    post<LicitacaoPipeline>({ action: 'lic_mover_etapa', id, para, ...extra }),
  /** Campos da ficha (responsável, datas, valor…) — a mesma action da lista de Oportunidades. */
  atualizar: (id: number, campos: Record<string, any>) => post({ action: 'atualizar', id, ...campos }),
  detalhe: (id: number) => post({ action: 'detalhe', id }),
  /** "Enviamos a proposta" (05/10/2026). `enviada_em` em parede BRT ('AAAA-MM-DDTHH:mm'). */
  registrarEnvio: (id: number, dados: { enviada_em?: string; protocolo?: string; preco_proposto?: number | null }) =>
    post<LicitacaoPipeline & { aviso?: string }>({ action: 'lic_registrar_envio', id, ...dados }),
  desfazerEnvio: (id: number) => post<LicitacaoPipeline>({ action: 'lic_desfazer_envio', id }),
  /** Estado do acompanhamento pelo PNCP + linha do tempo da licitação. */
  acompanhamento: (id: number) => post<Acompanhamento>({ action: 'lic_acompanhamento', id }),
  acompanharAgora: (id: number) => post<{ status: string; mensagem: string }>({ action: 'lic_acompanhar_agora', id }),
  definirLinkPncp: (id: number, link: string) => post<Acompanhamento>({ action: 'lic_acompanhamento_referencia', id, link }),
  vistoTudo: (id: number) => post({ action: 'ack_todos_eventos_licitacao', oportunidade_id: id }),
  vendedores: async (): Promise<Vendedor[]> => {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/comercial/vendedores`, { headers: { Authorization: token } });
    const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
    return Array.isArray(d) ? d : (d?.vendedores || []);
  },
};
